import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/base/ToastContainer';
import { laboratoriosService } from '../../../services/laboratorios.service';
import { medicamentosService } from '../../../services/medicamentos.service';

interface SmartPasteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (items: any[]) => void;
}

type ColumnMapping = 'ignore' | 'producto' | 'nombreComercial' | 'laboratorio' | 'precioUnitario' | 'cum' | 'iva' | 'cantidad';

interface ParsedItem {
    producto: string;
    nombreComercial: string;
    laboratorio: string;
    precioUnitario: number;
    cum: string;
    iva: number;
    cantidad: number;
    // Validation fields
    validLaboratory?: any;
    validCum?: any;
    hasErrors?: boolean;
}

export default function SmartPasteModal({ isOpen, onClose, onSuccess }: SmartPasteModalProps) {
    const { showToast } = useToast();
    const [step, setStep] = useState<1 | 2 | 3>(1);

    // Step 1 state
    const [rawText, setRawText] = useState('');
    const [pastedRows, setPastedRows] = useState<string[][]>([]);

    // Step 2 state
    const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);

    // Step 3 state
    const [processedItems, setProcessedItems] = useState<ParsedItem[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen]);

    const resetState = () => {
        setStep(1);
        setRawText('');
        setPastedRows([]);
        setColumnMappings([]);
        setProcessedItems([]);
        setIsProcessing(false);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const clipboardData = e.clipboardData.getData('text');
        if (!clipboardData) return;

        const rows = clipboardData.split('\n')
            .map(row => row.split('\t'))
            .filter(row => row.length > 0 && row.some(cell => cell.trim().length > 0)); // remove empty rows

        if (rows.length > 0) {
            setPastedRows(rows);
            // Initialize mappings based on number of columns in first row
            setColumnMappings(Array(rows[0].length).fill('ignore'));
            setStep(2);
        } else {
            showToast('No se detectaron datos válidos', 'error');
        }
    };

    const handleMappingChange = (index: number, value: ColumnMapping) => {
        const newMappings = [...columnMappings];
        newMappings[index] = value;
        setColumnMappings(newMappings);
    };

    const cleanNumber = (val: string) => {
        if (!val) return 0;

        // Remove currency symbols, spaces, letters
        let cleaned = val.replace(/[$A-Za-z\s]/g, '');

        // Find the last index of dot and comma
        const lastDot = cleaned.lastIndexOf('.');
        const lastComma = cleaned.lastIndexOf(',');

        // If there are no dots or commas, it's a clean integer
        if (lastDot === -1 && lastComma === -1) {
            const num = parseFloat(cleaned);
            return isNaN(num) ? 0 : num;
        }

        // Determine which one is the decimal separator (the one that comes last)
        const hasDot = lastDot !== -1;
        const hasComma = lastComma !== -1;

        if (hasDot && hasComma) {
            // Both exist. The one at the end is the decimal separator.
            if (lastDot > lastComma) {
                // US format: 1,234.56
                cleaned = cleaned.replace(/,/g, '');
            } else {
                // EU/COL format: 1.234,56
                cleaned = cleaned.replace(/\./g, '');
                cleaned = cleaned.replace(',', '.');
            }
        } else if (hasDot) {
            const parts = cleaned.split('.');
            const lastPart = parts[parts.length - 1];

            if (parts.length > 2 || lastPart.length === 3) {
                // likely thousands separators
                cleaned = cleaned.replace(/\./g, '');
            } else {
                // likely decimal separator
                // do nothing, already a valid float string
            }
        } else if (hasComma) {
            const parts = cleaned.split(',');
            const lastPart = parts[parts.length - 1];

            if (parts.length > 2 || lastPart.length === 3) {
                // likely thousands separators
                cleaned = cleaned.replace(/,/g, '');
            } else {
                // likely decimal separator
                cleaned = cleaned.replace(/,/g, '.');
            }
        }

        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
    };

    const processData = async () => {
        // Encontrar índices de cada columna
        const indices = {
            producto: columnMappings.indexOf('producto'),
            nombreComercial: columnMappings.indexOf('nombreComercial'),
            laboratorio: columnMappings.indexOf('laboratorio'),
            precioUnitario: columnMappings.indexOf('precioUnitario'),
            cum: columnMappings.indexOf('cum'),
            iva: columnMappings.indexOf('iva'),
            cantidad: columnMappings.indexOf('cantidad'),
        };

        if (indices.producto === -1 && indices.cum === -1) {
            showToast('Debe mapear al menos Producto o CUM para identificar el ítem', 'error');
            return;
        }

        setIsProcessing(true);
        setStep(3);

        try {
            const items: ParsedItem[] = pastedRows.map(row => ({
                producto: indices.producto !== -1 ? (row[indices.producto] || '').trim() : '',
                nombreComercial: indices.nombreComercial !== -1 ? (row[indices.nombreComercial] || '').trim() : '',
                laboratorio: indices.laboratorio !== -1 ? (row[indices.laboratorio] || '').trim() : '',
                precioUnitario: indices.precioUnitario !== -1 ? cleanNumber(row[indices.precioUnitario] || '0') : 0,
                cum: indices.cum !== -1 ? (row[indices.cum] || '').trim() : '',
                iva: indices.iva !== -1 ? cleanNumber(row[indices.iva] || '0') : 0,
                cantidad: indices.cantidad !== -1 ? Math.max(1, cleanNumber(row[indices.cantidad] || '1')) : 1,
            })).filter(item => item.producto || item.cum); // Filter out empty after extraction

            // Validation loops
            // 1. Validar Laboratorios (Fuzzy search)
            const labPromises = items.map(async (item) => {
                if (item.laboratorio) {
                    try {
                        const result = await laboratoriosService.searchFuzzy(item.laboratorio);
                        if (result) item.validLaboratory = result;
                    } catch (e) {
                        // Ignore API errors for fuzzy search
                    }
                }
                return item;
            });

            await Promise.all(labPromises);

            // 2. Validar CUMs en batch
            const cumsToValidate = items.map(i => i.cum).filter(c => c && c.length > 0);
            if (cumsToValidate.length > 0) {
                try {
                    const validationResults = await medicamentosService.validarCums(cumsToValidate);
                    // Match validation back to items
                    items.forEach(item => {
                        if (item.cum) {
                            const validMatch = validationResults.find(v => v.cum === item.cum);
                            if (validMatch) {
                                item.validCum = validMatch;
                                if (!item.producto) item.producto = validMatch.nombre; // autofill
                            } else {
                                item.hasErrors = true; // CUM no encontrado
                            }
                        }
                    });
                } catch (e) {
                    console.error("Error validando CUMs:", e);
                }
            }

            // Flag items without matched laboratory as warning/error
            items.forEach(item => {
                if (item.laboratorio && !item.validLaboratory) {
                    item.hasErrors = true;
                }
            });

            setProcessedItems(items);
        } catch (error) {
            console.error(error);
            showToast('Error procesando los datos', 'error');
            setStep(2); // Go back if complete failure
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfirm = () => {
        // Prepare data for Cotizacion form
        const finalData = processedItems.map(item => ({
            producto: item.producto,
            nombreComercial: item.nombreComercial,
            medicamentoId: item.validCum?.id || '', // Si encontró el cum, poner su id
            laboratorio: item.laboratorio,
            validLaboratory: item.validLaboratory,
            laboratorioId: item.validLaboratory?.idlab || '',
            cum: item.cum || item.validCum?.cum || '', // Add CUM here
            cantidad: item.cantidad,
            precioUnitario: item.precioUnitario,
            iva: item.iva,
        }));
        onSuccess(finalData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}></div>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl relative z-10 flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <i className="ri-file-excel-line text-green-600"></i>
                        Importar desde Excel
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-red-500 transition-colors">
                        <i className="ri-close-line text-xl"></i>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {/* Stepper Header */}
                    <div className="flex items-center justify-center mb-8">
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-teal-500 text-white' : 'bg-slate-200 text-slate-500'} font-bold`}>1</div>
                        <div className={`w-16 h-1 mx-2 ${step >= 2 ? 'bg-teal-500' : 'bg-slate-200'}`}></div>
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-teal-500 text-white' : 'bg-slate-200 text-slate-500'} font-bold`}>2</div>
                        <div className={`w-16 h-1 mx-2 ${step >= 3 ? 'bg-teal-500' : 'bg-slate-200'}`}></div>
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? 'bg-teal-500 text-white' : 'bg-slate-200 text-slate-500'} font-bold`}>3</div>
                    </div>
                    <div className="flex justify-between w-64 mx-auto mb-8 text-xs font-medium text-slate-500">
                        <span className={step >= 1 ? 'text-teal-600' : ''}>Pegar</span>
                        <span className={step >= 2 ? 'text-teal-600' : ''}>Mapear</span>
                        <span className={step >= 3 ? 'text-teal-600' : ''}>Validar</span>
                    </div>

                    {step === 1 && (
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-8 bg-slate-50 relative group">
                            <i className="ri-clipboard-line text-4xl text-slate-400 mb-4 group-hover:text-teal-500 transition-colors"></i>
                            <h4 className="text-lg font-semibold text-slate-700 mb-2">Haz clic aquí y presiona Ctrl+V</h4>
                            <p className="text-sm text-slate-500 text-center max-w-md">
                                Selecciona las celdas en tu Excel (deben incluir encabezados si los hay, o solo los datos), cópialas y pégalas en este recuadro.
                            </p>
                            <textarea
                                autoFocus
                                value={rawText}
                                onChange={(e) => setRawText(e.target.value)}
                                onPaste={handlePaste}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer resize-none"
                            ></textarea>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="flex flex-col h-full">
                            <div className="mb-4">
                                <h4 className="text-md font-semibold text-slate-800">Mapea las columnas</h4>
                                <p className="text-sm text-slate-500">Identifica qué dato corresponde a cada columna de tu Excel.</p>
                            </div>

                            <div className="overflow-x-auto border border-slate-200 rounded-lg">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-slate-50 sticky top-0 shadow-sm z-10">
                                        <tr>
                                            {columnMappings.map((mapping, colIndex) => (
                                                <th key={colIndex} className="p-3 border-b border-r border-slate-200 min-w-[150px]">
                                                    <select
                                                        value={mapping}
                                                        onChange={(e) => handleMappingChange(colIndex, e.target.value as ColumnMapping)}
                                                        className={`w-full p-2 border rounded focus:ring-teal-500 focus:border-teal-500 ${mapping !== 'ignore' ? 'bg-teal-50 border-teal-200 text-teal-800 font-medium' : 'bg-white border-slate-300'}`}
                                                    >
                                                        <option value="ignore">Ignorar Columna</option>
                                                        <option value="producto">Nombre / Producto</option>
                                                        <option value="nombreComercial">Nom. Comercial</option>
                                                        <option value="laboratorio">Laboratorio</option>
                                                        <option value="cantidad">Cantidad</option>
                                                        <option value="precioUnitario">Precio Unit.</option>
                                                        <option value="iva">IVA %</option>
                                                        <option value="cum">CUM</option>
                                                    </select>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {pastedRows.slice(0, 10).map((row, rowIndex) => (
                                            <tr key={rowIndex} className={rowIndex === 0 ? 'bg-yellow-50/50' : ''}>
                                                {columnMappings.map((_, colIndex) => (
                                                    <td key={colIndex} className="p-3 border-r border-slate-100 truncate max-w-[200px]" title={row[colIndex] || ''}>
                                                        {row[colIndex] || <span className="text-slate-300 italic">Vacio</span>}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {pastedRows.length > 10 && (
                                <p className="text-xs text-slate-500 mt-2 text-center">Mostrando solo las primeras 10 de {pastedRows.length} filas.</p>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="flex flex-col h-full">
                            {isProcessing ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin mb-4"></i>
                                    <p className="text-slate-600 font-medium">Procesando y validando {pastedRows.length} filas...</p>
                                    <p className="text-sm text-slate-500 mt-2">Cruzando laboratorios y validando CUMs con el servidor.</p>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-md font-semibold text-slate-800">Resultados de Validación</h4>
                                        <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-bold">
                                            {processedItems.length} ítems procesados
                                        </span>
                                    </div>

                                    <div className="overflow-y-auto max-h-[400px] border border-slate-200 rounded-lg">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm border-b border-slate-200">
                                                <tr>
                                                    <th className="p-3 font-semibold text-slate-600">Estado</th>
                                                    <th className="p-3 font-semibold text-slate-600">Producto</th>
                                                    <th className="p-3 font-semibold text-slate-600">Laboratorio</th>
                                                    <th className="p-3 font-semibold text-slate-600">CUM</th>
                                                    <th className="p-3 font-semibold text-slate-600">Cant.</th>
                                                    <th className="p-3 font-semibold text-slate-600">Precio / IVA</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {processedItems.map((item, idx) => (
                                                    <tr key={idx} className={item.hasErrors ? 'bg-amber-50' : 'bg-white'}>
                                                        <td className="p-3">
                                                            {item.hasErrors ? (
                                                                <span className="text-amber-500 flex items-center" title="Requiere revisión manual post-importación">
                                                                    <i className="ri-error-warning-fill mr-1"></i> Revisar
                                                                </span>
                                                            ) : (
                                                                <span className="text-green-500 flex items-center">
                                                                    <i className="ri-checkbox-circle-fill mr-1"></i> OK
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="p-3 truncate max-w-[200px]" title={item.producto}>{item.producto || '-'}</td>
                                                        <td className="p-3">
                                                            <div className="flex flex-col">
                                                                <span className="truncate max-w-[150px]" title={item.laboratorio}>{item.laboratorio || '-'}</span>
                                                                {item.laboratorio && item.validLaboratory ? (
                                                                    <span className="text-xs text-green-600"><i className="ri-check-line"></i> Match: {item.validLaboratory.nombre}</span>
                                                                ) : item.laboratorio ? (
                                                                    <span className="text-xs text-amber-600"><i className="ri-error-warning-line"></i> No match (se creará/ignorará)</span>
                                                                ) : null}
                                                            </div>
                                                        </td>
                                                        <td className="p-3">
                                                            <div className="flex flex-col">
                                                                <span>{item.cum || '-'}</span>
                                                                {item.cum && item.validCum ? (
                                                                    <span className="text-xs text-green-600"><i className="ri-check-line"></i> Válido</span>
                                                                ) : item.cum ? (
                                                                    <span className="text-xs text-amber-600"><i className="ri-error-warning-line"></i> No encontrado</span>
                                                                ) : null}
                                                            </div>
                                                        </td>
                                                        <td className="p-3 text-center">{item.cantidad}</td>
                                                        <td className="p-3">
                                                            ${item.precioUnitario.toLocaleString()} ({item.iva}%)
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {processedItems.some(i => i.hasErrors) && (
                                        <div className="mt-4 p-3 bg-amber-100 border border-amber-200 text-amber-800 rounded-lg text-sm flex items-start">
                                            <i className="ri-information-fill text-lg mr-2 mt-0.5"></i>
                                            <p>
                                                Algunos registros tienen problemas (laboratorio no encontrado o CUM inválido). Serán importados, pero marcados en el formulario para que puedas corregirlos o crear el laboratorio si hace falta.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between rounded-b-xl">
                    <button
                        onClick={step === 1 ? onClose : () => setStep(prev => (prev - 1) as any)}
                        className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                        disabled={isProcessing}
                    >
                        {step === 1 ? 'Cancelar' : 'Atrás'}
                    </button>

                    {step === 2 && (
                        <button
                            onClick={processData}
                            className="px-6 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors flex items-center"
                        >
                            Procesar <i className="ri-arrow-right-line ml-2"></i>
                        </button>
                    )}

                    {step === 3 && !isProcessing && (
                        <button
                            onClick={handleConfirm}
                            className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors flex items-center shadow-sm"
                        >
                            <i className="ri-file-download-line mr-2"></i> Importar a Cotización
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
